import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, defer, of, throwError, timer } from 'rxjs';
import { catchError, finalize, map, retry, shareReplay, switchMap, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import type { ApiSuccess } from '../models/api-response.model';
import type {
  CodigoPermiso,
  Configuracion2fa,
  DatosRegistro,
  Login2faRequerido,
  LoginRespuesta,
  Permisos,
  SesionRespuesta,
  Usuario,
  VendedorSesion,
} from '../models/sesion.model';

export interface Credenciales {
  identificador: string; // email o teléfono (SPEC RN-SEG-02)
  password: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  // accessToken SOLO en memoria (CLAUDE.md §5.1). El refresh vive en la cookie httpOnly
  // vitrina_refresh, que este service nunca lee.
  private readonly _usuario = signal<Usuario | null>(null);
  private readonly _vendedor = signal<VendedorSesion | null>(null);
  private readonly _permisos = signal<Permisos | null>(null);
  private readonly _esDueno = signal(false);
  private readonly _accessToken = signal<string | null>(null);

  readonly usuario = this._usuario.asReadonly();
  readonly vendedor = this._vendedor.asReadonly();
  readonly permisos = this._permisos.asReadonly();
  readonly esDueno = this._esDueno.asReadonly();
  readonly accessToken = this._accessToken.asReadonly();
  readonly autenticado = computed(() => this._usuario() !== null);
  readonly tipo = computed(() => this._usuario()?.tipo ?? null);

  // Un solo POST /auth/refresh en vuelo aunque varias requests reciban 401 a la vez
  private refreshEnVuelo$: Observable<string> | null = null;

  tienePermiso(codigo: CodigoPermiso): boolean {
    if (this._esDueno()) return true;
    return this._permisos()?.[codigo] === true;
  }

  login(credenciales: Credenciales): Observable<SesionRespuesta | Login2faRequerido> {
    return this.http
      .post<ApiSuccess<LoginRespuesta | Login2faRequerido>>(`${environment.apiUrl}/auth/login`, credenciales)
      .pipe(
        map((res) => res.data),
        tap((data) => {
          if (!('requiere_2fa' in data)) this.aplicarSesion(data);
        }),
      );
  }

  // `recordar`: no pedir el código al entrar en este navegador por 30 días (D-144)
  login2fa(token2fa: string, codigo: string, recordar = false): Observable<SesionRespuesta> {
    return this.http
      .post<ApiSuccess<LoginRespuesta>>(`${environment.apiUrl}/auth/login/2fa`, { token_2fa: token2fa, codigo, recordar })
      .pipe(
        map((res) => res.data),
        tap((data) => this.aplicarSesion(data)),
      );
  }

  // Registro de vendedor: el backend deja la sesión iniciada (RN-VEN-03)
  registro(datos: DatosRegistro): Observable<SesionRespuesta> {
    return this.http.post<ApiSuccess<LoginRespuesta>>(`${environment.apiUrl}/auth/registro`, datos).pipe(
      map((res) => res.data),
      tap((data) => this.aplicarSesion(data)),
    );
  }

  // Área de inicio según el tipo de usuario
  rutaInicio(): string {
    const tipo = this.tipo();
    if (tipo === 'INTERNO') return '/admin';
    if (tipo === 'VENDEDOR') return '/vendedor';
    return '/';
  }

  cambiarPassword(passwordActual: string, passwordNueva: string): Observable<SesionRespuesta> {
    return this.http
      .post<ApiSuccess<SesionRespuesta>>(`${environment.apiUrl}/auth/cambiar-password`, { password_actual: passwordActual, password_nueva: passwordNueva })
      .pipe(
        map((res) => res.data),
        tap((data) => this.aplicarSesion(data)),
      );
  }

  // RN-SEG-04: la respuesta es la misma exista o no la cuenta
  olvidePassword(email: string): Observable<string> {
    return this.http.post<ApiSuccess<{ mensaje: string }>>(`${environment.apiUrl}/auth/forgot-password`, { email }).pipe(map((res) => res.data.mensaje));
  }

  restablecerPassword(token: string, password: string): Observable<string> {
    return this.http.post<ApiSuccess<{ mensaje: string }>>(`${environment.apiUrl}/auth/reset-password`, { token, password }).pipe(map((res) => res.data.mensaje));
  }

  // ── 2FA (RN-SEG-03) ─────────────────────────────────────────────────────────
  configurar2fa(passwordActual: string): Observable<Configuracion2fa> {
    return this.http.post<ApiSuccess<Configuracion2fa>>(`${environment.apiUrl}/auth/2fa/configurar`, { password_actual: passwordActual }).pipe(map((res) => res.data));
  }

  // Activa y recarga la sesión (totp_activo cambia); devuelve los códigos de recuperación
  activar2fa(codigo: string): Observable<string[]> {
    return this.http.post<ApiSuccess<{ codigos_recuperacion: string[] }>>(`${environment.apiUrl}/auth/2fa/activar`, { codigo }).pipe(
      map((res) => res.data.codigos_recuperacion),
      switchMap((codigos) => this.cargarSesion().pipe(map(() => codigos))),
    );
  }

  desactivar2fa(passwordActual: string, codigo: string): Observable<SesionRespuesta> {
    return this.http
      .post<ApiSuccess<SesionRespuesta>>(`${environment.apiUrl}/auth/2fa/desactivar`, { password_actual: passwordActual, codigo })
      .pipe(
        map((res) => res.data),
        tap((data) => this.aplicarSesion(data)),
      );
  }

  regenerarCodigos(passwordActual: string): Observable<string[]> {
    return this.http
      .post<ApiSuccess<{ codigos_recuperacion: string[] }>>(`${environment.apiUrl}/auth/2fa/codigos-recuperacion`, { password_actual: passwordActual })
      .pipe(map((res) => res.data.codigos_recuperacion));
  }

  // Cierra la sesión en todos los dispositivos (incluido este)
  logoutTodas(): Observable<number> {
    return this.http.post<ApiSuccess<{ sesiones_cerradas: number }>>(`${environment.apiUrl}/auth/logout/all`, {}).pipe(
      map((res) => res.data.sesiones_cerradas),
      tap(() => this.limpiarSesion()),
    );
  }

  // La request sale ANTES de limpiar el estado para que lleve el Authorization (patrón VendIA)
  logout(): void {
    this.http.post(`${environment.apiUrl}/auth/logout`, {}).subscribe({ error: () => undefined });
    this.limpiarSesion();
  }

  /**
   * Cierra sesión y recarga en `destino`, ESPERANDO al backend: si la página se recarga con la
   * petición en vuelo, el navegador la cancela, la cookie del refresh sigue viva y la sesión
   * se restaura sola al volver (pasaba en el celular). Máximo 4 s de espera.
   */
  salirA(destino: string): void {
    this.http
      .post(`${environment.apiUrl}/auth/logout`, {})
      .pipe(
        timeout(4000),
        catchError(() => of(null)),
      )
      .subscribe(() => {
        this.limpiarSesion();
        location.assign(destino);
      });
  }

  refreshToken(): Observable<string> {
    if (this.refreshEnVuelo$) return this.refreshEnVuelo$;

    this.refreshEnVuelo$ = this.http
      .post<ApiSuccess<{ accessToken: string }>>(`${environment.apiUrl}/auth/refresh`, {})
      .pipe(
        map((res) => res.data.accessToken),
        tap((token) => this._accessToken.set(token)),
        finalize(() => {
          this.refreshEnVuelo$ = null;
        }),
        shareReplay(1),
      );
    return this.refreshEnVuelo$;
  }

  // Al cargar la app: POST /auth/refresh → GET /auth/me (CLAUDE.md §5.1). Sin sesión no se
  // muestra ningún error; solo un 401 real invalida la sesión (un 5xx por deploy no).
  // D-162: si el servidor está ocupado o no responde (429, 0, 5xx), se reintenta dos veces antes
  // de seguir sin sesión; así una pestaña nueva no manda a iniciar sesión por un momento malo.
  restaurarSesion(): Observable<void> {
    return defer(() => this.refreshToken()).pipe(
      retry({
        count: 2,
        // El error llega ya normalizado por error.interceptor ({ status, code, message })
        delay: (err: unknown, intento: number) => {
          const status = (err as { status?: number } | null)?.status ?? -1;
          return status === 429 || status === 0 || status >= 500 ? timer(intento * 2500) : throwError(() => err);
        },
      }),
      switchMap(() => this.cargarSesion()),
      map(() => undefined),
      catchError((err: unknown) => {
        if ((err as { status?: number } | null)?.status === 401) this.limpiarSesion();
        return of(undefined);
      }),
    );
  }

  cargarSesion(): Observable<SesionRespuesta> {
    return this.http.get<ApiSuccess<SesionRespuesta>>(`${environment.apiUrl}/auth/me`).pipe(
      map((res) => res.data),
      tap((data) => this.aplicarSesion(data)),
    );
  }

  limpiarSesion(): void {
    this._usuario.set(null);
    this._vendedor.set(null);
    this._permisos.set(null);
    this._esDueno.set(false);
    this._accessToken.set(null);
  }

  private aplicarSesion(data: SesionRespuesta & { accessToken?: string }): void {
    this._usuario.set(data.usuario);
    this._vendedor.set(data.vendedor);
    this._permisos.set(data.permisos);
    this._esDueno.set(data.es_dueno);
    if (data.accessToken) this._accessToken.set(data.accessToken);
  }
}
